import {
  ChangeDetectorRef, Component, EventEmitter, Input, OnChanges, OnDestroy,
  OnInit, Optional, Output, SimpleChanges, ViewChild
} from '@angular/core';
import { forkJoin, from, interval, Observable, of, Subject } from 'rxjs';
import { catchError, concatMap, finalize, takeUntil, tap } from 'rxjs/operators';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { BaseChartDirective } from 'ng2-charts';
import {
  CurrentSensorData, HistoricalSensorData, SensorDataService, SensorHealthStatus
} from 'app/core/device/services/sensor-data.service';
import { DeviceCurrentPosition, DeviceService } from 'app/core/device/services/device.service';
import {
  AlarmConfigurationType, AlarmService, DeviceAlarmConfiguration
} from 'app/core/alarm/services/alarm.service';
import { LocationPhotoService, LocationPhotoSlot } from 'app/core/installation/services/location-photo.service';
import { timestamp } from 'app/core/_base/util/date.util';

type MonitorTab = 'alarms' | 'history' | 'voltage' | 'magnetic' | 'altitude' | 'location' | 'configuration' | 'information';
type HistoryRange = '24h' | '7d' | '30d' | 'custom';

export interface SensorDataRow {
  id?: number;
  create_date?: string | Date;
  write_date?: string | Date;
  value: string | number | null;
  device_id?: number;
  sensor_id?: number;
  sensor_type_id?: number;
  siac_device_sensor_type_id?: number;
  health_status?: SensorHealthStatus;
}

interface SensorPoint { date: Date; value: number; }
interface SensorStatistics {
  current: number | null;
  maximum: number | null;
  minimum: number | null;
  average: number | null;
  range: number | null;
  variation: number | null;
  sampleCount: number;
  lastUpdate: Date | null;
}
interface LocationStatusView { name: string; cssClass: string; }
interface OverallHealthView extends LocationStatusView { detail: string; icon: string; }
interface HealthIndicatorView {
  sensorId: number;
  title: string;
  icon: string;
  value: string;
  range: string;
  status: SensorHealthStatus;
  statusLabel: string;
  hasValue: boolean;
}
/** Presentación de cada tipo de `siac_alarms_types` en el tab Configuración. */
interface AlarmTypePresentation {
  title: string;
  icon: string;
  /** `event`: se dispara por un sensor bool; `threshold`: depende de un umbral; `position`: zona de control. */
  kind: 'event' | 'threshold' | 'position';
  unit?: string;
  /** Umbral propio del equipo en `siac_devices`, prioritario sobre `alarm_th` de la configuración. */
  deviceThreshold?: keyof DeviceAlarmConfiguration['device'];
  /** Sin umbral, la tarjeta pide revisarlo. */
  thresholdRequired?: boolean;
  /** Aviso fijo mientras la referencia del tipo se encuentre en revisión. */
  reviewNote?: string;
}

interface AlarmConfigCardView {
  id: number;
  title: string;
  icon: string;
  subtitle: string;
  value: string;
  status: 'ok' | 'warning' | 'missing';
  statusLabel: string;
}

interface PositionAlarmCardView {
  title: string;
  icon: string;
  subtitle: string;
  threshold: number | null;
  distance: number | null;
  margin: number | null;
  progress: number;
  zone: 'inside' | 'outside' | 'unknown';
}

interface UnassignedAlarmCardView {
  id: number;
  title: string;
  icon: string;
  threshold: string;
  detail: string;
}

interface DevicePositionView {
  latitude: number;
  longitude: number;
  create_date?: string | Date;
  source?: 'gps' | 'installation' | 'location';
}
interface MetricView {
  label: string;
  title: string;
  description: string;
  emptyTitle: string;
  unit: string;
  sensorId: number;
  icon: string;
  color: string;
}

@Component({
  selector: 'app-battery-voltage-modal',
  templateUrl: './battery-voltage-modal.component.html',
  styleUrls: ['./battery-voltage-modal.component.scss']
})
export class BatteryVoltageModalComponent implements OnChanges, OnInit, OnDestroy {
  /** Fallback visual para activos sin imagen o con una imagen no disponible. */
  readonly siacIconPath = 'assets/images/icons/icon-siac.png';
  readonly SENSOR_BATTERY = 5;
  readonly SENSOR_BATTERY_LEVEL = 30;
  readonly SENSOR_RSSI = 7;
  // Magnetómetro X/Y/Z y Variación de altura ya no se grafican
  // readonly SENSOR_MAG_X = 24;
  // readonly SENSOR_MAG_Y = 25;
  // readonly SENSOR_MAG_Z = 26;
  readonly SENSOR_ACCEL_STATUS = 27;
  readonly SENSOR_ALTITUDE = 31;
  // readonly SENSOR_ALTITUDE_DELTA = 32;
  readonly SENSOR_MAG_DELTA = 33;
  /** `rssi_db`: RSSI ya expresado en dB por la ingesta, a diferencia del valor crudo del sensor 7. */
  readonly SENSOR_RSSI_DB = 38;
  /** Valor crítico de RSSI indicado por operación; no existe columna `installation_*` para él. */
  readonly RSSI_CRITICAL_DB = -7;
  readonly POSITION_INDICATOR_ID = -1;
  /** Referencia fija del voltaje de batería; `siac_devices` no tiene una columna `installation_*` para este valor. */
  readonly BATTERY_REFERENCE_VOLTAGE = 3.3;
  readonly REFERENCE_LINE_COLOR = '#facc15';
  /** Color de las líneas fijas de límite (voltaje 3.3 V y RSSI crítico). */
  readonly LIMIT_LINE_COLOR = '#ef4444';
  // readonly HISTORY_SENSOR_IDS = [5, 31, 24, 25, 26, 32, 33, 38];
  readonly HISTORY_SENSOR_IDS = [5, 31, 33, 38];
  readonly historyMetrics: MetricView[] = [
    { label: 'Voltaje de batería', title: 'Voltaje de batería en el tiempo', description: 'Evolución de las lecturas de batería.',
      emptyTitle: 'Sin datos de voltaje', unit: 'V', sensorId: 5, icon: '⚡', color: '#22c55e' },
    { label: 'Altura', title: 'Altura en el tiempo', description: 'Evolución histórica de la altitud GNSS.',
      emptyTitle: 'Sin datos de altura', unit: 'm', sensorId: 31, icon: '↕', color: '#60a5fa' },
    // { label: 'Magnetómetro X', title: 'Magnetómetro X en el tiempo', description: 'Lecturas históricas del eje magnético X.',
    //   emptyTitle: 'Sin datos de MAG X', unit: 'LSB', sensorId: 24, icon: 'X', color: '#22c55e' },
    // { label: 'Magnetómetro Y', title: 'Magnetómetro Y en el tiempo', description: 'Lecturas históricas del eje magnético Y.',
    //   emptyTitle: 'Sin datos de MAG Y', unit: 'LSB', sensorId: 25, icon: 'Y', color: '#60a5fa' },
    // { label: 'Magnetómetro Z', title: 'Magnetómetro Z en el tiempo', description: 'Lecturas históricas del eje magnético Z.',
    //   emptyTitle: 'Sin datos de MAG Z', unit: 'LSB', sensorId: 26, icon: 'Z', color: '#f59e0b' },
    // { label: 'Variación de altura', title: 'Variación de altura en el tiempo', description: 'Diferencia entre la altitud actual y la referencia.',
    //   emptyTitle: 'Sin datos de variación de altura', unit: 'm', sensorId: 32, icon: '↕', color: '#a78bfa' },
    { label: 'Variación magnética', title: 'Variación magnética en el tiempo', description: 'Cambio del campo magnético respecto de la referencia.',
      emptyTitle: 'Sin datos de variación magnética', unit: 'LSB', sensorId: 33, icon: '⌁', color: '#f97316' },
    { label: 'RSSI', title: 'RSSI en el tiempo', description: 'Intensidad de la señal recibida en dB.',
      emptyTitle: 'Sin datos de RSSI', unit: 'dB', sensorId: 38, icon: '▥', color: '#38bdf8' }
  ];

  @Input() location: any;
  @Input() device: any;
  @Input() alarmList: any[] = [];
  @Input() initialTab: MonitorTab = 'history';
  @Input() markerIconUrl = '';
  @Output() closeRequested = new EventEmitter<void>();
  @ViewChild('historyChart', {read: BaseChartDirective}) historyChart?: BaseChartDirective;
  @ViewChild('alarmChart', {read: BaseChartDirective}) alarmChart?: BaseChartDirective;

  activeTab: MonitorTab = 'history';
  selectedHistorySensorId = this.SENSOR_BATTERY;
  historyRange: HistoryRange = '24h';
  customStart = '';
  customEnd = '';
  loading = false;
  loadErrors: string[] = [];
  invalidReadingCount = 0;
  batteryPercentage: number | null = null;
  batteryPercentageEstimated = false;
  batteryAutonomyDays: number | null = null;
  batteryAutonomyDetail = 'No hay suficientes datos para estimar la autonomía.';
  currentRows: CurrentSensorData[] = [];
  historyBySensor: { [sensorId: number]: SensorPoint[] } = {};

  chartData: any[] = [];
  chartLabels: string[] = [];
  chartColors: any[] = [];
  chartOptions: any = {};
  chartLegend = true;
  chartType = 'line';
  chartPlugins: any[] = [];
  chartReady = false;
  alarmChartData: any[] = [];
  alarmChartColors: any[] = [];
  alarmChartOptions: any = {};
  alarmChartLegend = true;
  alarmChartType = 'line';
  alarmChartReady = false;
  alarmChartTypes: string[] = [];
  alarmRange: HistoryRange = '24h';
  alarmCustomStart = '';
  alarmCustomEnd = '';
  alarmLoading = false;
  alarmLoadError = '';
  /** Cruce de tipos de alarma y configuraciones del equipo; se carga al abrir el tab Configuración. */
  alarmConfiguration: DeviceAlarmConfiguration | null = null;
  alarmConfigurationLoading = false;
  alarmConfigurationError = '';
  private alarmConfigurationDeviceId: number | null = null;
  /** Tipos de alarma ocultos por ahora en el tab Configuración (1 = Activación). */
  private readonly HIDDEN_ALARM_TYPE_IDS = [1];
  /** Presentación por `alarm_type_id`; los tipos nuevos usan un icono y título genéricos. */
  private readonly ALARM_TYPE_VIEWS: { [alarmTypeId: number]: AlarmTypePresentation } = {
    1: { title: 'Activación', icon: 'power', kind: 'event' },
    2: { title: 'Batería baja', icon: 'battery', kind: 'threshold', thresholdRequired: true },
    3: { title: 'Desplazamiento de zona de control', icon: 'map-pin', kind: 'position', unit: 'm',
      deviceThreshold: 'position_alarm_threshold_m' },
    4: { title: 'Movimiento', icon: 'move', kind: 'event' },
    5: { title: 'Cambio de orientación', icon: 'rotate-cw', kind: 'threshold', unit: '°',
      deviceThreshold: 'accel_angle_threshold' },
    6: { title: 'Alteración magnética', icon: 'compass', kind: 'threshold', unit: 'LSB',
      deviceThreshold: 'mag_alarm_threshold', thresholdRequired: true, reviewNote: 'Revisar referencia' },
    7: { title: 'Cambio de altura', icon: 'triangle', kind: 'threshold', unit: 'm',
      deviceThreshold: 'altitude_alarm_threshold_m', thresholdRequired: true }
  };
  assetImageLoadFailed = false;
  assetTypeIconLoadFailed = false;
  readonly PHOTO_SLOTS: LocationPhotoSlot[] = [1, 2];
  readonly MAX_PHOTO_BYTES = 5 * 1024 * 1024;
  /** Fotos de instalación cargadas para el carrusel; el icono del activo solo se usa sin fotos. */
  photoSlides: Array<{ photo: LocationPhotoSlot; url: SafeUrl }> = [];
  photoIndex = 0;
  /** Mini modal de "Subir fotos" abierto desde la pestaña Información. */
  photoDialogOpen = false;
  /** Archivos elegidos en el mini modal que aún no se han subido, por posición. */
  photoDrafts: { [slot: number]: { file: File; url: SafeUrl; objectUrl: string } } = {};
  photoSaving = false;
  photoError = '';
  /** URLs `blob:` vigentes; se revocan al reemplazarlas o al cerrar el modal. */
  private photoObjectUrls: string[] = [];
  private photoRequestLocationId: number | null = null;
  readonly locationMapOptions: google.maps.MapOptions = {
    mapTypeId: 'satellite', disableDefaultUI: true, zoomControl: true, clickableIcons: false
  };
  readonly locationMapZoom = 19;
  locationMapCenter: google.maps.LatLngLiteral = { lat: 0, lng: 0 };
  locationMarkerOptions: google.maps.MarkerOptions = {};
  /** Posición consultada directamente del dispositivo, cuando la API la entrega. */
  private livePosition: DeviceCurrentPosition | null | undefined = undefined;

  private readonly destroy$ = new Subject<void>();
  private monitorRequestInProgress = false;
  private positionRequestInProgress = false;
  private alarmRequestInProgress = false;
  private alarmChartResizeFrame: number | null = null;
  private alarmChartResizeSecondFrame: number | null = null;

  constructor(
    private sensorDataService: SensorDataService,
    private alarmService: AlarmService,
    private cdr: ChangeDetectorRef,
    private locationPhotoService: LocationPhotoService,
    private sanitizer: DomSanitizer,
    @Optional() private deviceService?: DeviceService
  ) {}

  ngOnInit(): void {
    this.activeTab = this.normalizeTab(this.initialTab);
    this.updateLocationMapCenter();
    this.updateLocationMarkerOptions();
    this.refreshAlarmChart();
    this.fetchAlarmData();
    this.fetchMonitorData();
    this.refreshDevicePosition();
    this.loadLocationPhotos();
    interval(10000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.fetchMonitorData(false);
      this.refreshDevicePosition();
    });
    interval(6000).pipe(takeUntil(this.destroy$)).subscribe(() => {
      if (this.photoSlides.length > 1) this.showNextPhoto();
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.alarmList) {
      this.refreshAlarmChart();
    }
    if (changes.initialTab && !changes.initialTab.firstChange) {
      this.selectTab(this.normalizeTab(changes.initialTab.currentValue));
    }
    const locationChanged = changes.location && !changes.location.firstChange;
    const deviceChanged = changes.device && !changes.device.firstChange;
    if (changes.location || changes.device) {
      this.updateLocationMapCenter();
    }
    if (changes.location || changes.device || changes.markerIconUrl) {
      this.updateLocationMarkerOptions();
    }
    if (locationChanged) {
      this.assetImageLoadFailed = false;
      this.assetTypeIconLoadFailed = false;
      if (Number(changes.location.previousValue?.id) !== Number(changes.location.currentValue?.id)) {
        this.loadLocationPhotos();
      }
    }
    if (locationChanged || deviceChanged) {
      if (deviceChanged || (locationChanged &&
          Number(changes.location.previousValue?.id) !== Number(changes.location.currentValue?.id))) {
        this.livePosition = undefined;
      }
      this.fetchMonitorData();
      this.fetchAlarmData();
      this.refreshDevicePosition();
      this.alarmConfiguration = null;
      this.alarmConfigurationDeviceId = null;
      if (this.activeTab === 'configuration') this.loadAlarmConfiguration();
    }
  }

  ngOnDestroy(): void {
    this.cancelAlarmChartResize();
    this.revokePhotoUrls();
    this.clearPhotoDrafts();
    this.destroy$.next();
    this.destroy$.complete();
  }

  /** Cualquier usuario autenticado puede subir fotos; solo se requiere una ubicación. */
  get canEditPhotos(): boolean {
    return !!this.location?.id;
  }

  hasPhoto(photo: LocationPhotoSlot): boolean {
    return this.photoSlides.some(slide => slide.photo === photo);
  }

  showNextPhoto(): void {
    if (!this.photoSlides.length) return;
    this.photoIndex = (this.photoIndex + 1) % this.photoSlides.length;
  }

  showPreviousPhoto(): void {
    if (!this.photoSlides.length) return;
    this.photoIndex = (this.photoIndex - 1 + this.photoSlides.length) % this.photoSlides.length;
  }

  get hasPhotoDrafts(): boolean {
    return Object.keys(this.photoDrafts).length > 0;
  }

  /** Vista previa de una posición: el archivo elegido o, si no hay, la foto ya subida. */
  photoPreview(photo: LocationPhotoSlot): SafeUrl | null {
    const draft = this.photoDrafts[photo];
    if (draft) return draft.url;
    const slide = this.photoSlides.find(item => item.photo === photo);
    return slide ? slide.url : null;
  }

  openPhotoDialog(): void {
    if (!this.canEditPhotos) return;
    this.photoError = '';
    this.photoDialogOpen = true;
  }

  closePhotoDialog(): void {
    if (this.photoSaving) return;
    this.clearPhotoDrafts();
    this.photoError = '';
    this.photoDialogOpen = false;
  }

  selectPhotoDraft(photo: LocationPhotoSlot, event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files && input.files[0];
    // Se limpia para permitir volver a elegir el mismo archivo.
    if (input) input.value = '';
    if (!file || this.photoSaving) return;

    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      this.photoError = 'La foto debe ser JPG, PNG o WEBP.';
      return;
    }
    if (file.size > this.MAX_PHOTO_BYTES) {
      this.photoError = 'La foto supera el máximo de 5 MB.';
      return;
    }

    this.photoError = '';
    this.discardPhotoDraft(photo);
    const objectUrl = URL.createObjectURL(file);
    this.photoDrafts = { ...this.photoDrafts, [photo]: { file, objectUrl, url: this.sanitizer.bypassSecurityTrustUrl(objectUrl) } };
  }

  discardPhotoDraft(photo: LocationPhotoSlot): void {
    const draft = this.photoDrafts[photo];
    if (!draft) return;
    URL.revokeObjectURL(draft.objectUrl);
    const drafts = { ...this.photoDrafts };
    delete drafts[photo];
    this.photoDrafts = drafts;
  }

  /** Sube en orden las fotos elegidas y recarga el carrusel con las versiones guardadas. */
  savePhotoDrafts(): void {
    const locationId = Number(this.location?.id);
    const slots = this.PHOTO_SLOTS.filter(photo => !!this.photoDrafts[photo]);
    if (!locationId || !slots.length || this.photoSaving) return;

    this.photoSaving = true;
    this.photoError = '';
    let failedPhoto: LocationPhotoSlot | null = null;
    from(slots).pipe(
      concatMap(photo => this.locationPhotoService.uploadPhoto(locationId, photo, this.photoDrafts[photo].file).pipe(
        tap(() => this.discardPhotoDraft(photo)),
        catchError(error => {
          failedPhoto = photo;
          throw error;
        })
      )),
      takeUntil(this.destroy$),
      finalize(() => {
        this.photoSaving = false;
        // Las fotos que sí se subieron deben verse aunque otra haya fallado.
        this.loadLocationPhotos(slots[0]);
      })
    ).subscribe(
      () => {},
      error => {
        this.photoError = `No fue posible subir la foto ${failedPhoto}${typeof error === 'string' ? `: ${error}` : ''}.`;
        this.cdr.detectChanges();
      },
      () => {
        this.photoDialogOpen = false;
        this.cdr.detectChanges();
      }
    );
  }

  private clearPhotoDrafts(): void {
    Object.keys(this.photoDrafts).forEach(key => URL.revokeObjectURL(this.photoDrafts[Number(key)].objectUrl));
    this.photoDrafts = {};
  }

  /**
   * Carga `installation_photo_1/2` de la ubicación. Una foto ausente o con error
   * se omite del carrusel; sin fotos se mantiene el icono del activo.
   * @param focusPhoto Foto que debe quedar visible tras la carga (por ejemplo, la recién subida).
   */
  private loadLocationPhotos(focusPhoto?: LocationPhotoSlot): void {
    const locationId = Number(this.location?.id);
    this.photoRequestLocationId = locationId || null;
    if (!locationId) {
      this.setPhotoSlides([]);
      return;
    }

    forkJoin(this.PHOTO_SLOTS.map(photo => this.locationPhotoService.getPhoto(locationId, photo).pipe(
      catchError(() => of(null))
    ))).pipe(takeUntil(this.destroy$)).subscribe((blobs: Array<Blob | null>) => {
      // Descarta respuestas de una ubicación anterior si el modal cambió mientras cargaba.
      if (this.photoRequestLocationId !== locationId) return;
      const slides = this.PHOTO_SLOTS
        .map((photo, index) => ({ photo, blob: blobs[index] }))
        .filter((slide): slide is { photo: LocationPhotoSlot; blob: Blob } => !!slide.blob);
      this.setPhotoSlides(slides, focusPhoto);
    });
  }

  private setPhotoSlides(slides: Array<{ photo: LocationPhotoSlot; blob: Blob }>, focusPhoto?: LocationPhotoSlot): void {
    this.revokePhotoUrls();
    this.photoSlides = slides.map(slide => {
      const url = URL.createObjectURL(slide.blob);
      this.photoObjectUrls.push(url);
      // Angular marcaría las URLs blob: como inseguras sin este bypass; el contenido proviene de la API.
      return { photo: slide.photo, url: this.sanitizer.bypassSecurityTrustUrl(url) };
    });
    const focusIndex = this.photoSlides.findIndex(slide => slide.photo === focusPhoto);
    this.photoIndex = focusIndex >= 0 ? focusIndex : Math.min(this.photoIndex, Math.max(0, this.photoSlides.length - 1));
    this.cdr.detectChanges();
  }

  private revokePhotoUrls(): void {
    this.photoObjectUrls.forEach(url => URL.revokeObjectURL(url));
    this.photoObjectUrls = [];
  }

  selectTab(tab: MonitorTab): void {
    if (tab === 'voltage') this.selectedHistorySensorId = this.SENSOR_BATTERY;
    // if (tab === 'magnetic') this.selectedHistorySensorId = this.SENSOR_MAG_X;
    if (tab === 'magnetic') this.selectedHistorySensorId = this.SENSOR_MAG_DELTA;
    if (tab === 'altitude') this.selectedHistorySensorId = this.SENSOR_ALTITUDE;
    this.activeTab = ['voltage', 'magnetic', 'altitude'].includes(tab) ? 'history' : tab;
    this.refreshChart();
    if (this.activeTab === 'configuration') this.loadAlarmConfiguration();
    if (this.activeTab === 'alarms') {
      this.refreshAlarmChart();
      this.scheduleAlarmChartResize();
    } else {
      this.cancelAlarmChartResize();
    }
  }

  selectHistoryVariable(sensorId: any): void {
    this.selectedHistorySensorId = Number(sensorId);
    this.refreshChart();
  }

  onHistoryRangeChange(): void {
    if (this.historyRange !== 'custom') this.fetchHistoryData();
  }

  /**
   * @brief Actualiza el rango consultado para el gráfico de alarmas.
   */
  onAlarmRangeChange(): void {
    if (this.alarmRange !== 'custom') this.fetchAlarmData();
  }

  /**
   * @brief Valida y aplica un rango personalizado para las alarmas.
   */
  applyAlarmCustomRange(): void {
    if (!this.resolveAlarmRange()) {
      this.alarmLoadError = 'El rango personalizado debe tener fechas válidas y la fecha inicial debe ser anterior a la final.';
      this.refreshAlarmChart();
      return;
    }
    this.fetchAlarmData();
  }

  applyCustomRange(): void {
    const range = this.resolveHistoryRange();
    if (!range) {
      this.loadErrors = ['El rango personalizado debe tener fechas válidas y la fecha inicial debe ser anterior a la final.'];
      return;
    }
    this.fetchHistoryData();
  }

  exportHistoryCsv(): void {
    const points = this.historyBySensor[this.activeMetric.sensorId] || [];
    if (!points.length) return;
    const escapeCsv = (value: any) => `"${String(value).replace(/"/g, '""')}"`;
    const rows = [
      ['Fecha', this.activeMetric.label, 'Unidad'],
      ...points.map(point => [point.date.toISOString(), point.value, this.activeMetric.unit])
    ];
    const csv = '\uFEFF' + rows.map(row => row.map(escapeCsv).join(',')).join('\r\n');
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${this.assetInternalCode}-${this.activeMetric.sensorId}-${this.historyRange}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  }

  requestClose(): void { this.closeRequested.emit(); }

  get activeDevice(): any {
    const locationDevices = this.location && (Array.isArray(this.location.Devices)
      ? this.location.Devices
      : Array.isArray(this.location.devices) ? this.location.devices : []);
    const requestedDevice = this.device && (!this.device.location_id || Number(this.device.location_id) === Number(this.location?.id))
      ? locationDevices.find(device => Number(device.id) === Number(this.device.id)) || this.device
      : null;
    if (requestedDevice) return requestedDevice;
    const linkedDevice = locationDevices.find(device => Number(device.location_id) === Number(this.location?.id));
    if (linkedDevice) return linkedDevice;
    return locationDevices[0] || this.device || null;
  }

  get assetName(): string {
    return this.location?.description || 'Activo sin descripción';
  }

  get assetInternalCode(): string {
    return this.location?.internal_code || 'Sin código interno';
  }

  get assetIdentifier(): string {
    const device = this.activeDevice || {};
    return device.identifier || device.code || device.name || (device.id ? `DISP-${device.id}` : 'Sin identificador');
  }

  get assetTypeName(): string {
    const locationType = this.locationTypeRelation;
    if (locationType?.description) return locationType.description;
    if (locationType?.name) return locationType.name;
    if (this.activeDevice?.DeviceType?.name) return this.activeDevice.DeviceType.name;
    const types = { 4: 'Transformador', 8: 'Camioneta', 9: 'Pala / equipo móvil' };
    return types[Number(this.location?.location_type_id)] || 'Activo monitoreado';
  }

  /**
   * @brief Indica si el dispositivo activo es un EcoCore.
   * @returns {boolean} true cuando la relación oficial DeviceType.name coincide.
   */
  get isEcoCore(): boolean {
    const deviceTypeName = this.activeDevice?.DeviceType?.name;
    return typeof deviceTypeName === 'string' && deviceTypeName.trim().toLowerCase() === 'ecocore';
  }

  get assetTypeIconUrl(): string {
    const icon = this.locationTypeRelation?.image_icon;
    const configuredIcon = typeof icon === 'string' ? icon.trim() : icon?.url || icon?.path || icon?.value;
    if (configuredIcon && !this.assetTypeIconLoadFailed) return configuredIcon;

    const fallbackIcons = {
      4: 'assets/icons/dash/transformador-green.png',
      8: 'assets/icons/dash/camioneta-green.png',
      9: 'assets/icons/dash/retroescavadora-green.png'
    };
    return fallbackIcons[Number(this.location?.location_type_id)] || this.siacIconPath;
  }

  handleAssetTypeIconError(): void {
    this.assetTypeIconLoadFailed = true;
  }

  get installationName(): string {
    const installationRelation = typeof this.location?.installation_id === 'object'
      ? this.location.installation_id : null;
    return installationRelation?.name || this.location?.Installation?.name
      || this.location?.installation?.name
      || this.location?.installation_name
      || (this.location?.installation_id && !installationRelation
        ? `Instalación ${this.location.installation_id}` : 'Sin información');
  }

  get assetModel(): string {
    return this.location?.model || 'Sin información';
  }

  get assetDescription(): string {
    return this.location?.description || 'Sin información';
  }

  get assetAddress(): string {
    return this.location?.address || 'Sin información';
  }

  get coordinates(): string {
    const position = this.resolvedDevicePosition;
    return position ? `${position.latitude.toFixed(6)}, ${position.longitude.toFixed(6)}` : 'Sin coordenadas';
  }

  get positionSourceLabel(): string {
    switch (this.resolvedDevicePosition?.source) {
      case 'gps': return 'GPS actual';
      case 'installation': return 'Referencia de instalación (sin lectura GPS vigente)';
      case 'location': return 'Coordenadas de la ubicación';
      default: return 'Sin coordenadas';
    }
  }

  get hasCoordinates(): boolean {
    return this.resolvedDevicePosition !== null;
  }

  get assetImageUrl(): string {
    const locationImage = this.location?.image;
    const configuredImage = typeof locationImage === 'string'
      ? locationImage.trim()
      : locationImage?.url || locationImage?.path || locationImage?.value;
    if (configuredImage && !this.assetImageLoadFailed) return configuredImage;

    const images = {
      4: 'assets/icons/dash/transformador-green.png',
      8: 'assets/icons/dash/camioneta-green.png',
      9: 'assets/icons/dash/retroescavadora-green.png'
    };
    return images[Number(this.location?.location_type_id)] || this.siacIconPath;
  }

  get hasLocationImage(): boolean {
    const image = this.location?.image;
    const configuredImage = typeof image === 'string' ? image.trim() : image?.url || image?.path || image?.value;
    return !!configuredImage && !this.assetImageLoadFailed;
  }

  handleAssetImageError(): void {
    this.assetImageLoadFailed = true;
  }

  get overallHealth(): OverallHealthView {
    const statuses = [
      // this.SENSOR_BATTERY, this.SENSOR_RSSI, this.SENSOR_MAG_DELTA,
      this.SENSOR_BATTERY, this.SENSOR_RSSI_DB, this.SENSOR_MAG_DELTA,
      this.SENSOR_ALTITUDE, this.SENSOR_ACCEL_STATUS
    ].map(sensorId => this.getSensorHealth(sensorId));

    if (Number(this.location?.siac_status_id) === 4 || statuses.includes('bad')) {
      return { name: 'ALARMADO', cssClass: 'status-alarm', icon: '!',
        detail: 'Existen valores actuales fuera de los rangos permitidos.' };
    }
    if (statuses.includes('warning')) {
      return { name: 'SALUD EN ADVERTENCIA', cssClass: 'status-warning', icon: '!',
        detail: 'Existen valores actuales fuera del rango óptimo.' };
    }
    if (statuses.includes('good')) {
      return { name: 'SALUD ÓPTIMA', cssClass: 'status-armed', icon: '✓',
        detail: 'No existen valores actuales fuera de los rangos óptimos.' };
    }
    return { name: 'SIN DATOS EVALUABLES', cssClass: 'status-unknown', icon: '—',
      detail: 'No hay lecturas actuales con evaluación de salud disponible.' };
  }

  get healthIndicators(): HealthIndicatorView[] {
    const definitions = [
      { sensorId: this.SENSOR_BATTERY, title: 'Voltaje de batería', icon: '⚡', unit: 'V' },
      // RSSI crudo (CSQ), cuya conversión depende del proveedor del módem.
      // { sensorId: this.SENSOR_RSSI, title: 'RSSI', icon: '▥', unit: '', ignoreConfiguredUnit: true },
      // RSSI en dB calculado por la ingesta; la salud se evalúa con los rangos del sensor 38.
      { sensorId: this.SENSOR_RSSI_DB, title: 'RSSI', icon: '▥', unit: 'dB', ignoreConfiguredUnit: true },
      { sensorId: this.SENSOR_MAG_DELTA, title: 'Variación magnética', icon: '⌁', unit: 'LSB' },
      { sensorId: this.SENSOR_ALTITUDE, title: 'Altura', icon: '↕', unit: 'm' },
      { sensorId: this.SENSOR_ACCEL_STATUS, title: 'Estado del acelerómetro', icon: '◈', unit: '' }
    ];

    const indicators = definitions.map(definition => {
      const row = this.getLatestCurrentRow(definition.sensorId);
      const value = this.parseSensorValue(row?.value);
      const status = row?.health_status || 'unavailable';
      // ignoreConfiguredUnit usa la unidad de la definición en vez de la del catálogo.
      const unit = definition.ignoreConfiguredUnit ? definition.unit : row?.SensorType?.unit || definition.unit;
      return {
        sensorId: definition.sensorId,
        title: definition.title,
        icon: definition.icon,
        value: this.formatIndicatorValue(definition.sensorId, value, unit),
        range: this.formatIndicatorRange(row, definition.unit, !!definition.ignoreConfiguredUnit),
        status,
        statusLabel: this.healthStatusLabel(status),
        hasValue: value !== null
      };
    });

    return this.isEcoCore ? indicators.concat(this.positionHealthIndicator) : indicators;
  }

  /**
   * @brief Indicador de posición del EcoCore frente a su referencia de instalación.
   * @returns {HealthIndicatorView} Distancia disponible o estado sin datos.
   */
  private get positionHealthIndicator(): HealthIndicatorView {
    const currentPosition = this.currentDevicePosition;
    const reference = this.installationReference;
    const usingInstallationFallback = !currentPosition && !!reference;
    const distance = currentPosition && reference ? this.positionDistanceMeters
      : usingInstallationFallback ? 0 : null;
    return {
      sensorId: this.POSITION_INDICATOR_ID,
      title: 'Posición',
      icon: '⌖',
      value: distance === null ? '--' : this.formatValue(distance, 'm', 1),
      range: usingInstallationFallback
        ? 'Referencia de instalación · Sin GPS actual'
        : distance === null ? 'Sin datos de posición' : 'Comparación pendiente de FCV',
      status: 'unavailable',
      statusLabel: usingInstallationFallback ? 'Sin GPS actual' : distance === null ? 'Sin datos' : 'Sin evaluación',
      hasValue: distance !== null
    };
  }

  get hasCurrentData(): boolean { return this.currentRows.length > 0; }

  get lastUpdate(): Date | null {
    // /instant/current expone los registros vigentes de siac_device_sensor_current.
    // La actualización del activo corresponde exclusivamente al create_date más reciente.
    const dates = this.currentRows.map(row => this.readCreateDate(row)).filter(date => date !== null) as Date[];
    return dates.length
      ? dates.reduce((latest, date) => date.getTime() > latest.getTime() ? date : latest, dates[0])
      : null;
  }

  get activeMetric(): MetricView {
    return this.historyMetrics.find(metric => metric.sensorId === this.selectedHistorySensorId) || this.historyMetrics[0];
  }

  get historyRangeLabel(): string {
    return { '24h': 'últimas 24 horas', '7d': 'últimos 7 días', '30d': 'últimos 30 días',
      custom: 'rango personalizado' }[this.historyRange];
  }

  get alarmRangeLabel(): string {
    return { '24h': 'últimas 24 horas', '7d': 'últimos 7 días', '30d': 'últimos 30 días',
      custom: 'rango personalizado' }[this.alarmRange];
  }

  get activeStats(): SensorStatistics {
    return this.calculateStatistics(this.historyBySensor[this.activeMetric.sensorId] || []);
  }

  get activeCurrentValue(): number | null { return this.getCurrentValue(this.activeMetric.sensorId); }

  get altitudeCurrent(): number | null {
    const sensorAltitude = this.getCurrentValue(this.SENSOR_ALTITUDE);
    return sensorAltitude !== null ? sensorAltitude : this.parseSensorValue(this.location?.high_m);
  }

  // get magneticCurrent(): { x: number | null; y: number | null; z: number | null; magnitude: number | null } {
  //   const x = this.getCurrentValue(this.SENSOR_MAG_X);
  //   const y = this.getCurrentValue(this.SENSOR_MAG_Y);
  //   const z = this.getCurrentValue(this.SENSOR_MAG_Z);
  //   return { x, y, z, magnitude: x !== null && y !== null && z !== null ? Math.sqrt(x * x + y * y + z * z) : null };
  // }

  // get magneticSampleCount(): number {
  //   return Math.max(
  //     (this.historyBySensor[this.SENSOR_MAG_X] || []).length,
  //     (this.historyBySensor[this.SENSOR_MAG_Y] || []).length,
  //     (this.historyBySensor[this.SENSOR_MAG_Z] || []).length
  //   );
  // }

  /**
   * Valor de referencia de la variable graficada.
   * El voltaje y el RSSI usan valores fijos; la altura usa `installation_altitude` y la variación
   * magnética `mag_alarm_threshold` de `siac_devices` cuando tienen valor, sin depender de `installation_valid`.
   */
  get activeReferenceValue(): number | null {
    const sensorId = this.activeMetric.sensorId;
    if (sensorId === this.SENSOR_BATTERY) return this.BATTERY_REFERENCE_VOLTAGE;
    if (sensorId === this.SENSOR_RSSI_DB) return this.RSSI_CRITICAL_DB;
    const device = this.activeDevice;
    if (!device) return null;
    if (sensorId === this.SENSOR_ALTITUDE) return this.parseSensorValue(device.installation_altitude);
    if (sensorId === this.SENSOR_MAG_DELTA) return this.parseSensorValue(device.mag_alarm_threshold);
    return null;
  }

  get activeReferenceLabel(): string {
    const sensorId = this.activeMetric.sensorId;
    if (sensorId === this.SENSOR_RSSI_DB) return 'Valor crítico';
    if (sensorId === this.SENSOR_MAG_DELTA) return 'Umbral de alarma';
    return 'Referencia';
  }

  get activeReferenceColor(): string {
    const sensorId = this.activeMetric.sensorId;
    return sensorId === this.SENSOR_BATTERY || sensorId === this.SENSOR_RSSI_DB || sensorId === this.SENSOR_MAG_DELTA
      ? this.LIMIT_LINE_COLOR : this.REFERENCE_LINE_COLOR;
  }

  formatValue(value: number | null, unit: string, decimals = 2): string {
    return value === null || value === undefined || !isFinite(value)
      ? '--' : `${value.toFixed(decimals)}${unit ? ` ${unit}` : ''}`;
  }

  variationClass(value: number | null): string {
    return value === null || Math.abs(value) < 0.005 ? 'neutral' : value > 0 ? 'positive' : 'negative';
  }

  variationIcon(value: number | null): string {
    return value === null || Math.abs(value) < 0.005 ? '—' : value > 0 ? '↑' : '↓';
  }

  private fetchMonitorData(showLoading = true): void {
    if (this.monitorRequestInProgress || this.loading) return;

    const device = this.activeDevice;
    if (!device?.id) {
      this.loadErrors = ['La ubicación no tiene un dispositivo asociado.'];
      return;
    }
    const range = this.resolveHistoryRange();
    if (!range) {
      this.loadErrors = ['El rango de fechas del histórico no es válido.'];
      return;
    }
    const endDate = range.endDate;
    const startDate = range.startDate;

    this.monitorRequestInProgress = true;
    if (showLoading) this.loading = true;
    this.loadErrors = [];
    this.invalidReadingCount = 0;
    if (showLoading) this.chartReady = false;

    forkJoin([
      this.safeRequest(this.sensorDataService.getCurrentSensorData({ deviceId: device.id }), 'indicadores actuales'),
      this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 5, startDate, endDate), 'voltaje de batería'),
      // this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 24, startDate, endDate), 'magnetómetro X'),
      // this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 25, startDate, endDate), 'magnetómetro Y'),
      // this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 26, startDate, endDate), 'magnetómetro Z'),
      this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 31, startDate, endDate), 'altura'),
      // this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 32, startDate, endDate), 'variación de altura'),
      this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 33, startDate, endDate), 'variación magnética'),
      this.safeRequest(this.sensorDataService.getSensorHistory(device.id, 38, startDate, endDate), 'RSSI')
    ]).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.monitorRequestInProgress = false;
        if (showLoading) this.loading = false;
        this.cdr.detectChanges();
      })
    ).subscribe((responses: any[]) => {
      this.currentRows = this.extractRows<CurrentSensorData>(responses[0]).filter(row =>
        !row.device_id || Number(row.device_id) === Number(device.id));
      // Al reactivar los sensores comentados, sus índices de `responses` deben seguir el orden del forkJoin.
      this.historyBySensor = {
        5: this.toPoints(this.extractRows<HistoricalSensorData>(responses[1])),
        // 24: this.toPoints(this.extractRows<HistoricalSensorData>(responses[...])),
        // 25: this.toPoints(this.extractRows<HistoricalSensorData>(responses[...])),
        // 26: this.toPoints(this.extractRows<HistoricalSensorData>(responses[...])),
        31: this.toPoints(this.extractRows<HistoricalSensorData>(responses[2])),
        // 32: this.toPoints(this.extractRows<HistoricalSensorData>(responses[...])),
        33: this.toPoints(this.extractRows<HistoricalSensorData>(responses[3])),
        38: this.toPoints(this.extractRows<HistoricalSensorData>(responses[4]))
      };
      this.updateBatteryInsights();
      this.refreshChart();
    });
  }

  private updateLocationMapCenter(): void {
    const position = this.resolvedDevicePosition;
    const lat = position?.latitude;
    const lng = position?.longitude;
    const nextCenter = {
      lat: Number.isFinite(lat) ? lat : 0,
      lng: Number.isFinite(lng) ? lng : 0
    };

    if (this.locationMapCenter.lat === nextCenter.lat && this.locationMapCenter.lng === nextCenter.lng) return;
    this.locationMapCenter = nextCenter;
  }

  private updateLocationMarkerOptions(): void {
    const iconUrl = this.markerIconUrl || this.assetTypeIconUrl;
    this.locationMarkerOptions = {
      title: this.assetName,
      icon: {
        url: iconUrl,
        scaledSize: { width: 40, height: 40 },
        labelOrigin: { x: 20, y: -10 }
      } as any
    };
  }

  /**
   * Actualiza la posición vigente sin volver a solicitar el histórico de sensores.
   * Consulta exclusivamente la posición vigente en /device/position/current.
   */
  private refreshDevicePosition(): void {
    if (this.positionRequestInProgress) return;
    if (!this.deviceService) return;
    const device = this.activeDevice;
    if (!device?.id) return;

    const deviceId = Number(device.id);
    if (!Number.isFinite(deviceId) || deviceId <= 0) return;
    this.positionRequestInProgress = true;
    this.deviceService.getCurrentPosition(deviceId).pipe(
      takeUntil(this.destroy$),
      // Un fallo temporal del endpoint no debe borrar la última posición válida.
      // `null` queda reservado para una respuesta exitosa sin GPS vigente.
      catchError(() => of(undefined)),
      finalize(() => { this.positionRequestInProgress = false; })
    ).subscribe((positionCurrent: DeviceCurrentPosition | null | undefined) => {
      const activeDevice = this.activeDevice;
      if (Number(activeDevice?.id) !== deviceId) return;
      if (positionCurrent === undefined) return;
      this.livePosition = positionCurrent;
      if (activeDevice) activeDevice.PositionCurrent = positionCurrent;
      this.updateLocationMapCenter();
      this.cdr.detectChanges();
    });
  }

  private fetchHistoryData(): void {
    const device = this.activeDevice;
    const range = this.resolveHistoryRange();
    if (!device?.id || !range) return;

    const labels = {
      5: 'voltaje de batería', 31: 'altura', 33: 'variación magnética', 38: 'RSSI'
      // 24: 'magnetómetro X', 25: 'magnetómetro Y', 26: 'magnetómetro Z', 32: 'variación de altura'
    };
    this.loading = true;
    this.loadErrors = [];
    this.invalidReadingCount = 0;

    forkJoin(this.HISTORY_SENSOR_IDS.map(sensorId => this.safeRequest(
      this.sensorDataService.getSensorHistory(device.id, sensorId, range.startDate, range.endDate), labels[sensorId]
    ))).pipe(
      takeUntil(this.destroy$),
      finalize(() => { this.loading = false; this.cdr.detectChanges(); })
    ).subscribe((responses: any[]) => {
      this.historyBySensor = this.HISTORY_SENSOR_IDS.reduce((history, sensorId, index) => {
        history[sensorId] = this.toPoints(this.extractRows<HistoricalSensorData>(responses[index]));
        return history;
      }, {} as { [sensorId: number]: SensorPoint[] });
      this.updateBatteryInsights();
      this.refreshChart();
    });
  }

  /**
   * @brief Carga las alarmas del activo para el período seleccionado.
   *
   * La consulta usa el mismo contrato paginado que el listado de alarmas y
   * conserva las relaciones necesarias para agrupar por sensor de origen.
   */
  get alarmConfigurationSubtitle(): string {
    const device = this.alarmConfiguration?.device || this.activeDevice;
    const name = device?.description || this.assetIdentifier;
    return device?.id ? `${name} · Dispositivo ${device.id}` : name;
  }

  /** Tipos con o sin configuración para el equipo, excepto la zona de control y los de alcance por verificar. */
  get alarmConfigurationCards(): AlarmConfigCardView[] {
    return this.visibleAlarmTypes
      .filter(type => type.status === 'missing' ||
        (type.status === 'configured' && this.alarmTypeView(type).kind !== 'position'))
      .map(type => this.toAlarmConfigCard(type));
  }

  /** Tarjeta ancha de zona de control, comparando el umbral con la última distancia GPS calculada. */
  get positionAlarmCard(): PositionAlarmCardView | null {
    const type = this.visibleAlarmTypes
      .find(item => item.status === 'configured' && this.alarmTypeView(item).kind === 'position');
    if (!type) return null;
    const view = this.alarmTypeView(type);
    const config = type.configs[0];
    const threshold = this.resolveAlarmThreshold(type).value;
    const distance = this.positionDistanceMeters;
    const hasComparison = threshold !== null && threshold > 0 && distance !== null;
    return {
      title: view.title,
      icon: view.icon,
      subtitle: `${this.alarmSensorsLabel(type)} · Alarma ${config.id}`,
      threshold,
      distance,
      margin: hasComparison ? threshold - distance : null,
      progress: hasComparison ? Math.min(distance / threshold, 1) * 100 : 0,
      zone: !hasComparison ? 'unknown' : distance <= threshold ? 'inside' : 'outside'
    };
  }

  /** Configuraciones de la compañía sin ubicación asociada: su alcance debe verificarse. */
  get unassignedAlarmCards(): UnassignedAlarmCardView[] {
    return this.visibleAlarmTypes
      .filter(type => type.status === 'unassigned')
      .map(type => {
        const view = this.alarmTypeView(type);
        const config = type.configs[0];
        return {
          id: type.id,
          title: view.title,
          icon: view.icon,
          threshold: config.alarm_th === null ? 'Sin umbral' : this.formatThreshold(config.alarm_th),
          detail: `Configuración ${config.id}, sin asociación de ubicación${config.sensors.length ? '' : ' ni sensor'}. `
            + 'Unidad y aplicabilidad pendientes de verificar.'
        };
      });
  }

  formatThreshold(value: number, unit = ''): string {
    const text = value.toLocaleString('es-CL', { maximumFractionDigits: 2 });
    return unit ? `${text} ${unit}` : text;
  }

  loadAlarmConfiguration(force = false): void {
    const deviceId = Number(this.activeDevice?.id);
    if (!deviceId) {
      this.alarmConfiguration = null;
      this.alarmConfigurationError = 'La ubicación no tiene un dispositivo asociado.';
      return;
    }
    if (!force && (this.alarmConfigurationLoading || this.alarmConfigurationDeviceId === deviceId)) return;

    this.alarmConfigurationDeviceId = deviceId;
    this.alarmConfigurationLoading = true;
    this.alarmConfigurationError = '';
    this.alarmService.getDeviceConfiguration(deviceId).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.alarmConfigurationLoading = false;
        this.cdr.detectChanges();
      })
    ).subscribe(configuration => {
      // Descarta la respuesta si el dispositivo cambió mientras se consultaba.
      if (this.alarmConfigurationDeviceId === deviceId) this.alarmConfiguration = configuration;
    }, () => {
      this.alarmConfiguration = null;
      this.alarmConfigurationDeviceId = null;
      this.alarmConfigurationError = 'No fue posible cargar la configuración de alarmas.';
    });
  }

  /** Tipos que se muestran en el tab; se excluyen los de `HIDDEN_ALARM_TYPE_IDS`. */
  private get visibleAlarmTypes(): AlarmConfigurationType[] {
    return (this.alarmConfiguration?.types || []).filter(type => !this.HIDDEN_ALARM_TYPE_IDS.includes(type.id));
  }

  private alarmTypeView(type: AlarmConfigurationType): AlarmTypePresentation {
    const title = (type.name || 'Alarma').toLowerCase().replace(/^\w/, letter => letter.toUpperCase());
    return this.ALARM_TYPE_VIEWS[type.id] || { title, icon: 'bell', kind: 'threshold' };
  }

  private alarmSensorsLabel(type: AlarmConfigurationType): string {
    const names = (type.configs[0]?.sensors || []).map(sensor => sensor.name).filter(Boolean);
    return names.length ? names.join(' · ') : type.name;
  }

  /** El umbral del equipo prevalece sobre el general (`alarm_th`) de la configuración. */
  private resolveAlarmThreshold(type: AlarmConfigurationType): { value: number | null; source: 'equipo' | 'general' | null } {
    const view = this.alarmTypeView(type);
    const deviceValue = view.deviceThreshold ? this.alarmConfiguration?.device?.[view.deviceThreshold] : null;
    if (typeof deviceValue === 'number') return { value: deviceValue, source: 'equipo' };
    const generalValue = type.configs[0]?.alarm_th;
    if (typeof generalValue === 'number') return { value: generalValue, source: 'general' };
    return { value: null, source: null };
  }

  private toAlarmConfigCard(type: AlarmConfigurationType): AlarmConfigCardView {
    const view = this.alarmTypeView(type);
    const card = { id: type.id, title: view.title, icon: view.icon, subtitle: this.alarmSensorsLabel(type) };
    if (type.status === 'missing') {
      return { ...card, value: 'Sin configuración', status: 'missing', statusLabel: 'No configurada' };
    }
    if (view.kind === 'event') {
      return { ...card, value: 'Por evento', status: 'ok', statusLabel: 'Configurada' };
    }
    const threshold = this.resolveAlarmThreshold(type);
    if (threshold.value === null) {
      return view.thresholdRequired
        ? { ...card, value: 'Sin umbral definido', status: 'warning', statusLabel: 'Revisar umbral' }
        : { ...card, value: 'Sin umbral general', status: 'ok', statusLabel: 'Configurada' };
    }
    const value = `${this.formatThreshold(threshold.value, view.unit)} (${threshold.source})`;
    return view.reviewNote
      ? { ...card, value, status: 'warning', statusLabel: view.reviewNote }
      : { ...card, value, status: 'ok', statusLabel: 'Configurada' };
  }

  private fetchAlarmData(): void {
    if (this.alarmRequestInProgress) return;

    const device = this.activeDevice;
    const range = this.resolveAlarmRange();
    if (!range) return;
    if (!device?.id && !this.location?.id) {
      this.alarmLoadError = 'La ubicación no tiene un dispositivo asociado.';
      this.alarmList = [];
      this.refreshAlarmChart();
      return;
    }

    const filter: any = {
      alarm_status_id: 1,
      simple: true,
      st: timestamp(range.startDate),
      et: timestamp(range.endDate)
    };
    if (device?.id) filter.device_id = device.id;
    else filter.location_id = this.location.id;

    this.alarmRequestInProgress = true;
    this.alarmLoading = true;
    this.alarmLoadError = '';
    this.alarmService.getPage({
      sortField: 'id',
      sortOrder: 'desc',
      pageSize: 0,
      pageNumber: 0,
      filter
    } as any).pipe(
      takeUntil(this.destroy$),
      finalize(() => {
        this.alarmRequestInProgress = false;
        this.alarmLoading = false;
        this.cdr.detectChanges();
        if (this.activeTab === 'alarms') this.scheduleAlarmChartResize();
      })
    ).subscribe((response: any) => {
      const items = this.extractRows<any>(response);
      this.alarmList = items.sort((first, second) => {
        const firstDate = this.readDate(first)?.getTime() || 0;
        const secondDate = this.readDate(second)?.getTime() || 0;
        return firstDate - secondDate;
      });
      this.refreshAlarmChart();
    }, () => {
      this.alarmLoadError = 'No fue posible cargar las alarmas para el período seleccionado.';
      this.alarmList = [];
      this.refreshAlarmChart();
    });
  }

  private resolveHistoryRange(): { startDate: Date; endDate: Date } | null {
    return this.resolvePeriodRange(this.historyRange, this.customStart, this.customEnd);
  }

  private resolveAlarmRange(): { startDate: Date; endDate: Date } | null {
    return this.resolvePeriodRange(this.alarmRange, this.alarmCustomStart, this.alarmCustomEnd);
  }

  private resolvePeriodRange(
    range: HistoryRange,
    customStart: string,
    customEnd: string
  ): { startDate: Date; endDate: Date } | null {
    if (range === 'custom') {
      const startDate = new Date(customStart);
      const endDate = new Date(customEnd);
      return !isNaN(startDate.getTime()) && !isNaN(endDate.getTime()) && startDate < endDate
        ? { startDate, endDate } : null;
    }
    const endDate = new Date();
    const hours = range === '7d' ? 24 * 7 : range === '30d' ? 24 * 30 : 24;
    return { startDate: new Date(endDate.getTime() - hours * 60 * 60 * 1000), endDate };
  }

  private safeRequest(request: Observable<any>, label: string): Observable<any> {
    return request.pipe(catchError(error => {
      this.loadErrors.push(this.apiErrorMessage(error, label));
      return of({ itemList: [] });
    }));
  }

  private extractRows<T>(response: any): T[] {
    if (response && Array.isArray(response.itemList)) return response.itemList;
    if (response && Array.isArray(response.items)) return response.items;
    return Array.isArray(response) ? response : [];
  }

  private toPoints(rows: SensorDataRow[]): SensorPoint[] {
    return (rows || []).map(row => {
      const date = this.readDate(row);
      const value = this.parseSensorValue(row.value);
      if (!date || value === null) { this.invalidReadingCount++; return null; }
      return { date, value };
    }).filter(point => point !== null).sort((a, b) => a.date.getTime() - b.date.getTime());
  }

  private readDate(row: SensorDataRow): Date | null {
    const raw = row && (row.create_date || row.write_date);
    const date = raw ? new Date(raw) : null;
    return date && !isNaN(date.getTime()) ? date : null;
  }

  private readCreateDate(row: SensorDataRow): Date | null {
    const date = row?.create_date ? new Date(row.create_date) : null;
    return date && !isNaN(date.getTime()) ? date : null;
  }

  private getSensorId(row: SensorDataRow): number {
    return Number(row.sensor_type_id || row.sensor_id || row.siac_device_sensor_type_id);
  }

  private getCurrentValue(sensorId: number): number | null {
    return this.parseSensorValue(this.getLatestCurrentRow(sensorId)?.value);
  }

  private parseSensorValue(value: string | number | null | undefined): number | null {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'string' && value.trim() === '') return null;
    const parsed = Number(typeof value === 'string' ? value.trim() : value);
    return Number.isFinite(parsed) ? parsed : null;
  }

  private getSensorHealth(sensorId: number): SensorHealthStatus {
    return this.getLatestCurrentRow(sensorId)?.health_status || 'unavailable';
  }

  /**
   * @brief Calcula la distancia entre la posición actual y la instalación del dispositivo.
   * @returns {number|null} Distancia en metros o null si falta algún dato.
   */
  private get positionDistanceMeters(): number | null {
    const position = this.currentDevicePosition;
    const reference = this.installationReference;
    if (!position || !reference) return null;

    const earthRadiusMeters = 6371000;
    const toRadians = (value: number) => value * Math.PI / 180;
    const deltaLatitude = toRadians(position.latitude - reference.latitude);
    const deltaLongitude = toRadians(position.longitude - reference.longitude);
    const latitudeA = toRadians(reference.latitude);
    const latitudeB = toRadians(position.latitude);
    const haversine = Math.sin(deltaLatitude / 2) ** 2
      + Math.cos(latitudeA) * Math.cos(latitudeB) * Math.sin(deltaLongitude / 2) ** 2;
    return 2 * earthRadiusMeters * Math.atan2(Math.sqrt(haversine), Math.sqrt(1 - haversine));
  }

  /**
   * @brief Lee la posición vigente anidada en el dispositivo cuando está disponible.
   * @returns {DevicePositionView|null} Posición GPS actual o null.
   */
  private get currentDevicePosition(): DevicePositionView | null {
    const position = this.positionPayload;
    return this.toPositionView(position?.google_lat, position?.google_lng, 'gps', position?.create_date);
  }

  /** Posición recibida por sondeo o anidada en el dispositivo inicial. */
  private get positionPayload(): any {
    return this.livePosition !== undefined ? this.livePosition : this.activeDevice?.PositionCurrent;
  }

  /**
   * Resuelve la coordenada que debe mostrarse en el mapa del modal.
   * El origen queda disponible para distinguir GPS vigente de fallbacks.
   */
  private get resolvedDevicePosition(): DevicePositionView | null {
    const current = this.currentDevicePosition;
    if (current) return current;

    const installation = this.installationReference;
    if (installation) return {...installation, source: 'installation'};

    return this.toPositionView(this.location?.google_lat, this.location?.google_lng, 'location');
  }

  private toPositionView(
    latitudeValue: string | number | null | undefined,
    longitudeValue: string | number | null | undefined,
    source: 'gps' | 'installation' | 'location',
    createDate?: string | Date
  ): DevicePositionView | null {
    const latitude = this.parseSensorValue(latitudeValue);
    const longitude = this.parseSensorValue(longitudeValue);
    if (latitude === null || longitude === null || latitude < -90 || latitude > 90 ||
        longitude < -180 || longitude > 180 || (latitude === 0 && longitude === 0)) {
      return null;
    }
    return {latitude, longitude, create_date: createDate, source};
  }

  /**
   * @brief Obtiene la referencia de instalación definida en siac_devices.
   * No depende de `installation_valid`, que solo indica que la instalación fue completada.
   * @returns {{latitude: number; longitude: number}|null} Coordenadas válidas de instalación.
   */
  private get installationReference(): {latitude: number; longitude: number} | null {
    const device = this.activeDevice;
    if (!device) return null;
    const latitude = this.parseSensorValue(device.installation_latitude);
    const longitude = this.parseSensorValue(device.installation_longitude);
    return latitude === null || longitude === null || latitude < -90 || latitude > 90 ||
      longitude < -180 || longitude > 180 || (latitude === 0 && longitude === 0)
      ? null
      : {latitude, longitude};
  }

  private getLatestCurrentRow(sensorId: number): CurrentSensorData | null {
    const rows = this.currentRows.filter(row => this.getSensorId(row) === sensorId)
      .sort((a, b) => (this.readDate(a)?.getTime() || 0) - (this.readDate(b)?.getTime() || 0));
    return rows.length ? rows[rows.length - 1] : null;
  }

  private updateBatteryInsights(): void {
    const batteryLevel = this.getCurrentValue(this.SENSOR_BATTERY_LEVEL);
    const voltageRow = this.getLatestCurrentRow(this.SENSOR_BATTERY);
    const voltagePoints = this.historyBySensor[this.SENSOR_BATTERY] || [];
    const currentVoltage = this.getCurrentValue(this.SENSOR_BATTERY)
      ?? (voltagePoints.length ? voltagePoints[voltagePoints.length - 1].value : null);
    const minVoltage = this.parseSensorValue(voltageRow?.SensorType?.min_value);
    const maxVoltage = this.parseSensorValue(voltageRow?.SensorType?.max_value);

    if (batteryLevel !== null) {
      this.batteryPercentage = Math.max(0, Math.min(100, batteryLevel));
      this.batteryPercentageEstimated = false;
    } else if (currentVoltage !== null && minVoltage !== null && maxVoltage !== null && maxVoltage > minVoltage) {
      const percentage = ((currentVoltage - minVoltage) / (maxVoltage - minVoltage)) * 100;
      this.batteryPercentage = Math.max(0, Math.min(100, percentage));
      this.batteryPercentageEstimated = true;
    } else {
      this.batteryPercentage = null;
      this.batteryPercentageEstimated = false;
    }

    this.batteryAutonomyDays = null;
    this.batteryAutonomyDetail = 'No hay suficientes datos para estimar la autonomía.';
    if (voltagePoints.length < 3 || currentVoltage === null || minVoltage === null) return;

    const firstTime = voltagePoints[0].date.getTime();
    const spanDays = (voltagePoints[voltagePoints.length - 1].date.getTime() - firstTime) / 86400000;
    if (spanDays < 0.25) {
      this.batteryAutonomyDetail = 'Se requieren al menos 6 horas de lecturas válidas.';
      return;
    }

    const samples = voltagePoints.map(point => ({
      day: (point.date.getTime() - firstTime) / 86400000,
      voltage: point.value
    }));
    const meanDay = samples.reduce((sum, sample) => sum + sample.day, 0) / samples.length;
    const meanVoltage = samples.reduce((sum, sample) => sum + sample.voltage, 0) / samples.length;
    const denominator = samples.reduce((sum, sample) => sum + Math.pow(sample.day - meanDay, 2), 0);
    const slope = denominator > 0
      ? samples.reduce((sum, sample) => sum + (sample.day - meanDay) * (sample.voltage - meanVoltage), 0) / denominator
      : 0;

    if (!Number.isFinite(slope) || slope >= -0.001) {
      this.batteryAutonomyDetail = 'No se detecta una tendencia sostenida de descarga.';
      return;
    }

    const days = (currentVoltage - minVoltage) / Math.abs(slope);
    if (!Number.isFinite(days) || days < 0) return;
    this.batteryAutonomyDays = days;
    this.batteryAutonomyDetail = `Estimación según la tendencia de ${this.historyRangeLabel}.`;
  }

  formatBatteryPercentage(): string {
    return this.batteryPercentage === null ? 'Sin datos' : `${Math.round(this.batteryPercentage)} %`;
  }

  formatBatteryAutonomy(): string {
    if (this.batteryAutonomyDays === null) return 'Sin estimación';
    if (this.batteryAutonomyDays < 1) return `${Math.max(1, Math.round(this.batteryAutonomyDays * 24))} h`;
    if (this.batteryAutonomyDays > 365) return '> 365 días';
    const roundedDays = Math.max(1, Math.round(this.batteryAutonomyDays));
    return `${roundedDays} ${roundedDays === 1 ? 'día' : 'días'}`;
  }

  private healthStatusLabel(status: SensorHealthStatus): string {
    const labels: { [key in SensorHealthStatus]: string } = {
      good: 'Óptimo', warning: 'Advertencia', bad: 'Alerta', unavailable: 'Sin evaluación'
    };
    return labels[status];
  }

  private formatIndicatorValue(sensorId: number, value: number | null, unit: string): string {
    if (value === null) return '--';
    if (sensorId === this.SENSOR_RSSI_DB) return `${value} ${unit}`;
    if (sensorId === this.SENSOR_ACCEL_STATUS) {
      if (value === 0) return 'Normal';
      if (value === 1) return 'Alarmado';
    }
    return this.formatValue(value, unit);
  }

  private formatIndicatorRange(
    row: CurrentSensorData | null,
    fallbackUnit: string,
    ignoreConfiguredUnit = false
  ): string {
    const sensorType = row?.SensorType;
    if (!sensorType) return 'Sin rango configurado';
    const unit = ignoreConfiguredUnit ? fallbackUnit : sensorType.unit || fallbackUnit;
    const min = sensorType.health_good_min;
    const max = sensorType.health_good_max;
    if (min === null || min === undefined || max === null || max === undefined) {
      return 'Sin rango óptimo configurado';
    }
    if (min === max) return `Valor óptimo: ${min}${unit ? ` ${unit}` : ''}`;
    return `Óptimo: ${min} - ${max}${unit ? ` ${unit}` : ''}`;
  }

  private apiErrorMessage(error: any, label: string): string {
    const body = error?.error;
    const detail = typeof body === 'string' ? body.trim() : body?.error || body?.message;
    return detail ? `No fue posible obtener ${label}: ${detail}` : `No fue posible obtener ${label}.`;
  }

  private refreshChart(): void {
    if (this.activeTab !== 'history') {
      this.chartReady = false;
      return;
    }

    const metric = this.activeMetric;
    const points = this.historyBySensor[metric.sensorId] || [];
    if (!points.length) {
      this.chartReady = false;
      this.chartData = [];
      this.chartLabels = [];
      this.cdr.detectChanges();
      return;
    }

    const reference = this.activeReferenceValue;
    const nextOptions = this.createChartOptions(metric.unit, points, reference);
    const updateExistingChart = this.chartReady && !!this.historyChart?.chart;

    this.chartData = [this.createDataset(points, metric.label, true)];
    this.chartColors = [this.createColor(metric.color, `${metric.color}1a` )];
    if (reference !== null) {
      this.chartData.push(this.createReferenceDataset(points, reference, this.activeReferenceLabel));
      this.chartColors.push(this.createColor(this.activeReferenceColor, 'transparent'));
    }
    if (updateExistingChart) {
      Object.keys(this.chartOptions).forEach(key => delete this.chartOptions[key]);
      Object.assign(this.chartOptions, nextOptions);
      Object.assign(this.historyChart.chart.options, nextOptions);
    } else {
      this.chartOptions = nextOptions;
    }
    this.chartLegend = false;
    this.chartReady = true;
    this.chartLabels = [];
    this.cdr.detectChanges();
  }

  /**
   * @brief Prepara el gráfico de alarmas del activo agrupando los eventos por sensor origen.
   *
   * Cada tipo recibido desde `SourceDeviceSensorType.name` se representa como una
   * serie independiente para que el mismo gráfico soporte sensores heterogéneos.
   */
  private refreshAlarmChart(): void {
    const range = this.resolveAlarmRange();
    if (!range) {
      this.alarmChartData = [];
      this.alarmChartColors = [];
      this.alarmChartOptions = {};
      this.alarmChartReady = false;
      return;
    }
    const start = range.startDate;
    const end = range.endDate;
    const alarmGroups = new Map<string, number[]>();

    (this.alarmList || []).forEach(alarm => {
      const date = this.readDate(alarm as SensorDataRow);
      if (!date || date < start || date > end) return;
      const sensorType = alarm?.SourceDeviceSensorType?.name || 'Sin información';
      const dates = alarmGroups.get(sensorType) || [];
      dates.push(date.getTime());
      alarmGroups.set(sensorType, dates);
    });

    this.alarmChartTypes = Array.from(alarmGroups.keys());
    if (!this.alarmChartTypes.length) {
      this.alarmChartData = [];
      this.alarmChartColors = [];
      this.alarmChartOptions = {};
      this.alarmChartReady = false;
      return;
    }

    const palette = ['#ef535d', '#f59e0b', '#60a5fa', '#22c55e', '#a78bfa', '#f97316', '#e879f9', '#14b8a6'];
    this.alarmChartData = this.alarmChartTypes.map((sensorType, index) => {
      const eventTimes = Array.from(new Set(alarmGroups.get(sensorType) || [])).sort((a, b) => a - b);
      const points: Array<{x: Date; y: number}> = [{x: start, y: 0}];
      eventTimes.forEach(time => {
        const eventDate = new Date(time);
        const resetDate = new Date(Math.min(time + 60000, end.getTime()));
        points.push({x: eventDate, y: 0}, {x: eventDate, y: 1});
        if (resetDate.getTime() > eventDate.getTime()) points.push({x: resetDate, y: 0});
      });
      points.push({x: end, y: 0});
      points.sort((a, b) => a.x.getTime() - b.x.getTime() || a.y - b.y);
      return {
        data: points,
        label: sensorType,
        alarmTimes: eventTimes,
        borderWidth: 2,
        fill: false,
        spanGaps: true,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHitRadius: 12
      };
    });
    this.alarmChartColors = this.alarmChartTypes.map((_, index) => {
      const color = palette[index % palette.length];
      return this.createColor(color, `${color}1a`);
    });
    const nextOptions = this.createAlarmChartOptions(start, end);
    const updateExistingChart = !!this.alarmChart?.chart;
    if (updateExistingChart) {
      Object.keys(this.alarmChartOptions).forEach(key => delete this.alarmChartOptions[key]);
      Object.assign(this.alarmChartOptions, nextOptions);
      Object.assign(this.alarmChart.chart.options, nextOptions);
      this.alarmChart.chart.stop();
      this.alarmChart.chart.update({duration: 0});
    } else {
      this.alarmChartOptions = nextOptions;
    }
    this.alarmChartReady = true;
    if (this.activeTab === 'alarms') this.scheduleAlarmChartResize();
  }

  /**
   * Chart.js 2 calcula el tamaño al crear el canvas. Al cambiar de pestaña el
   * contenedor flex aún puede estar en una medida intermedia, dejando el
   * canvas estirado. Se esperan dos frames para que terminen el layout y la
   * detección de cambios antes de sincronizar el gráfico.
   */
  private scheduleAlarmChartResize(): void {
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') return;

    this.cancelAlarmChartResize();
    this.alarmChartResizeFrame = window.requestAnimationFrame(() => {
      this.alarmChartResizeFrame = null;
      this.alarmChartResizeSecondFrame = window.requestAnimationFrame(() => {
        this.alarmChartResizeSecondFrame = null;
        if (this.activeTab !== 'alarms' || !this.alarmChartReady) return;
        const chart = this.alarmChart?.chart;
        if (!chart) return;
        chart.stop();
        chart.resize();
        chart.update({duration: 0});
      });
    });
  }

  private cancelAlarmChartResize(): void {
    if (typeof window === 'undefined' || typeof window.cancelAnimationFrame !== 'function') return;
    if (this.alarmChartResizeFrame !== null) {
      window.cancelAnimationFrame(this.alarmChartResizeFrame);
      this.alarmChartResizeFrame = null;
    }
    if (this.alarmChartResizeSecondFrame !== null) {
      window.cancelAnimationFrame(this.alarmChartResizeSecondFrame);
      this.alarmChartResizeSecondFrame = null;
    }
  }

  /**
   * @brief Configura el eje temporal, el rango binario y el tooltip del gráfico de alarmas.
   * @param {Date} start - Inicio del período mostrado.
   * @param {Date} end - Fin del período mostrado.
   * @returns {any} Opciones compatibles con Chart.js 2 y ng2-charts.
   */
  private createAlarmChartOptions(start: Date, end: Date): any {
    const minutes = Math.max(1, (end.getTime() - start.getTime()) / 60000);
    const interval = this.getXAxisInterval(minutes);
    return {
      responsive: true,
      maintainAspectRatio: false,
      // El gráfico se muestra al cambiar de pestaña; evitar la animación de
      // entrada impide que un tamaño intermedio se pinte como una deformación.
      animation: {duration: 0},
      responsiveAnimationDuration: 0,
      elements: {
        line: {tension: 0},
        point: {radius: 0, hoverRadius: 5, hitRadius: 12}
      },
      tooltips: {
        enabled: true,
        mode: 'nearest',
        intersect: false,
        callbacks: {
          title: (items: any[], data: any) => {
            const item = items && items.length ? items[0] : null;
            return item ? this.formatAlarmTooltipDate(this.getAlarmTooltipDate(item, data)) : '';
          },
          label: (item: any, data: any) => {
            const dataset = data && data.datasets ? data.datasets[item.datasetIndex] : null;
            const pointDate = this.getAlarmTooltipDate(item, data);
            const pointTime = pointDate ? pointDate.getTime() : NaN;
            const isAlarmPoint = Number(item && item.yLabel) > 0 ||
              (dataset && Array.isArray(dataset.alarmTimes) && dataset.alarmTimes.some(time => Math.abs(time - pointTime) < 1000));
            const status = isAlarmPoint ? 'Alarma detectada' : 'Sin alarma';
            const sensorLabel = dataset && dataset.label ? dataset.label : 'Sin información';
            const dateLabel = this.formatAlarmTooltipDate(pointDate);
            // Cuando no hay tipo de sensor, la hora del punto permite identificar el evento igualmente.
            return sensorLabel === 'Sin información' && dateLabel
              ? `${sensorLabel}: ${status} · ${dateLabel}`
              : `${sensorLabel}: ${status}`;
          }
        }
      },
      hover: {mode: 'nearest', intersect: false},
      scales: {
        xAxes: [{
          type: 'time',
          distribution: 'linear',
          gridLines: {color: 'rgba(148, 163, 184, .10)'},
          time: {
            min: start,
            max: end,
            unit: interval.unit,
            stepSize: interval.stepSize,
            displayFormats: {minute: 'HH:mm', hour: 'HH:mm', day: 'DD/MM'},
            tooltipFormat: 'DD/MM/YYYY HH:mm:ss'
          },
          ticks: {fontColor: '#94a3b8', maxRotation: 0, minRotation: 0}
        }],
        yAxes: [{
          ticks: {
            min: 0,
            max: 1,
            stepSize: 1,
            fontColor: '#94a3b8',
            callback: (value: number) => String(value)
          },
          gridLines: {color: 'rgba(148, 163, 184, .16)'}
        }]
      },
      legend: {
        display: this.alarmChartTypes.length > 0,
        position: 'bottom',
        labels: {fontColor: '#cbd5e1', boxWidth: 12, padding: 14}
      }
    };
  }

  /**
   * @brief Obtiene la fecha exacta del punto activo en el tooltip de alarmas.
   * @param {any} item - Elemento seleccionado por Chart.js.
   * @param {any} data - Datos de las series renderizadas.
   * @returns {Date|null} Fecha del punto o null si no se puede resolver.
   */
  private getAlarmTooltipDate(item: any, data: any): Date | null {
    const dataset = data && data.datasets ? data.datasets[item && item.datasetIndex] : null;
    const pointIndex = item && Number.isInteger(item.index) ? item.index : item && item._index;
    const point = dataset && Array.isArray(dataset.data) && pointIndex !== undefined
      ? dataset.data[pointIndex]
      : null;
    const rawTime = point && point.x !== undefined
      ? point.x
      : item && item.xValue !== undefined && item.xValue !== null
        ? item.xValue
        : item && item.xLabel;
    const timestamp = rawTime instanceof Date ? rawTime.getTime() : new Date(rawTime).getTime();
    return isNaN(timestamp) ? null : new Date(timestamp);
  }

  /**
   * @brief Formatea la fecha exacta mostrada al pasar sobre un evento.
   * @param {any} value - Fecha entregada por la escala temporal de Chart.js.
   * @returns {string} Fecha y hora localizadas.
   */
  private formatAlarmTooltipDate(value: any): string {
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return '';
    const dateLabel = `${String(date.getDate()).padStart(2, '0')}/${String(date.getMonth() + 1).padStart(2, '0')}/${date.getFullYear()}`;
    const timeLabel = `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}:${String(date.getSeconds()).padStart(2, '0')}`;
    return `${dateLabel} ${timeLabel}`;
  }

  private createDataset(points: SensorPoint[], label: string, fill: boolean): any {
    return { data: points.map(point => ({ x: point.date, y: point.value })), label, borderWidth: 2, fill, spanGaps: true };
  }

  private createColor(borderColor: string, backgroundColor: string): any {
    return { borderColor, backgroundColor, pointBackgroundColor: borderColor, pointBorderColor: '#fff' };
  }

  /** Línea horizontal punteada que cubre el mismo intervalo que las lecturas graficadas. */
  private createReferenceDataset(points: SensorPoint[], reference: number, label: string): any {
    return {
      data: [{ x: points[0].date, y: reference }, { x: points[points.length - 1].date, y: reference }],
      label, isReference: true, borderWidth: 1.5, borderDash: [6, 4], fill: false,
      lineTension: 0, pointRadius: 0, pointHoverRadius: 0, pointHitRadius: 0
    };
  }

  private createChartOptions(unit: string, points: SensorPoint[], reference: number | null = null): any {
    const times = points.map(point => point.date.getTime());
    const minutes = times.length > 1 ? Math.max(1, (Math.max.apply(Math, times) - Math.min.apply(Math, times)) / 60000) : 60;
    const interval = this.getXAxisInterval(minutes);
    const values = points.map(point => point.value);
    // La referencia se incluye en la escala para que la línea siempre quede visible.
    const yRange = this.getYAxisRange(reference !== null ? values.concat(reference) : values);
    return {
      responsive: true, maintainAspectRatio: false, animation: { duration: 650, easing: 'easeOutQuart' },
      elements: { line: { tension: .25 }, point: { radius: 0, hoverRadius: 5, hitRadius: 12 } },
      // El modo `index` emparejaría la referencia por posición; su valor se muestra en el badge del encabezado.
      tooltips: { mode: 'index', intersect: false, filter: (item, data) => !data.datasets[item.datasetIndex].isReference, callbacks: {
        label: function(item, data) { return `${data.datasets[item.datasetIndex].label}: ${Number(item.yLabel).toFixed(2)} ${unit}`; }
      }},
      hover: { mode: 'nearest', intersect: false },
      scales: {
        xAxes: [{ type: 'time', distribution: 'linear', gridLines: { color: 'rgba(148,163,184,.10)' },
          time: { unit: interval.unit, stepSize: interval.stepSize,
            displayFormats: { minute: 'HH:mm', hour: 'HH:mm', day: 'DD/MM' }, tooltipFormat: 'DD/MM/YYYY HH:mm' },
          ticks: { fontColor: '#94a3b8', maxRotation: 0, minRotation: 0 } }],
        yAxes: [{ scaleLabel: { display: true, labelString: unit, fontColor: '#94a3b8' },
          ticks: { min: yRange.min, max: yRange.max, stepSize: yRange.stepSize, fontColor: '#94a3b8',
            callback: value => `${Number(value).toFixed(2)} ${unit}` },
          gridLines: { color: 'rgba(148,163,184,.10)' } }]
      }, legend: { display: false, labels: { fontColor: '#cbd5e1' } }
    };
  }

  private getXAxisInterval(minutes: number): { unit: string; stepSize: number } {
    if (minutes <= 240) return { unit: 'minute', stepSize: 30 };
    if (minutes <= 480) return { unit: 'hour', stepSize: 1 };
    if (minutes <= 1440) return { unit: 'hour', stepSize: 2 };
    if (minutes <= 4320) return { unit: 'hour', stepSize: 6 };
    return { unit: 'day', stepSize: 1 };
  }

  private getYAxisRange(values: number[]): { min: number; max: number; stepSize: number } {
    if (!values.length) return { min: 0, max: 1, stepSize: .2 };
    const min = Math.min.apply(Math, values);
    const max = Math.max.apply(Math, values);
    const range = Math.max(max - min, Math.abs(max) * .1, 1);
    const padding = range * .18;
    const step = this.getNiceStep(range + padding * 2, 5);
    return {
      min: Number((Math.floor((min - padding) / step) * step).toFixed(6)),
      max: Number((Math.ceil((max + padding) / step) * step).toFixed(6)), stepSize: step
    };
  }

  private getNiceStep(range: number, ticks: number): number {
    const rough = Math.max(range, .0001) / Math.max(2, ticks);
    const magnitude = Math.pow(10, Math.floor(Math.log(rough) / Math.LN10));
    const normalized = rough / magnitude;
    const nice = normalized <= 1 ? 1 : normalized <= 2 ? 2 : normalized <= 2.5 ? 2.5 : normalized <= 5 ? 5 : 10;
    return Number((nice * magnitude).toFixed(6));
  }

  private calculateStatistics(points: SensorPoint[]): SensorStatistics {
    if (!points.length) return this.emptyStatistics();
    const values = points.map(point => point.value);
    const maximum = Math.max.apply(Math, values);
    const minimum = Math.min.apply(Math, values);
    const current = values[values.length - 1];
    return { current, maximum, minimum,
      average: values.reduce((sum, value) => sum + value, 0) / values.length,
      range: maximum - minimum, variation: current - values[0], sampleCount: values.length,
      lastUpdate: points[points.length - 1].date };
  }

  private emptyStatistics(): SensorStatistics {
    return { current: null, maximum: null, minimum: null, average: null, range: null,
      variation: null, sampleCount: 0, lastUpdate: null };
  }

  private normalizeTab(tab: any): MonitorTab {
    if (tab === 'voltage') this.selectedHistorySensorId = this.SENSOR_BATTERY;
    // if (tab === 'magnetic') this.selectedHistorySensorId = this.SENSOR_MAG_X;
    if (tab === 'magnetic') this.selectedHistorySensorId = this.SENSOR_MAG_DELTA;
    if (tab === 'altitude') this.selectedHistorySensorId = this.SENSOR_ALTITUDE;
    if (['voltage', 'magnetic', 'altitude'].includes(tab)) return 'history';
    return ['alarms', 'history', 'location', 'configuration', 'information'].includes(tab) ? tab : 'history';
  }

  private get allHistoryPoints(): SensorPoint[] {
    return Object.keys(this.historyBySensor)
      .reduce((all, key) => all.concat(this.historyBySensor[Number(key)] || []), [] as SensorPoint[]);
  }

  private get locationTypeRelation(): any {
    if (typeof this.location?.location_type_id === 'object') return this.location.location_type_id;
    return this.location?.LocationType || this.location?.locationType || this.location?.location_type
      || this.location?.SiacLocationType || this.location?.siac_location_type || null;
  }
}
